import React, { useState } from 'react';
import { X, Network, ArrowRightLeft } from 'lucide-react';
import { CustomDropdown } from '../../components/CustomDropdown ';

interface LinkData {
    connection_type: string;
    is_uplink: boolean;
}

interface TopologyLinkModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: LinkData) => void;
    sourceName?: string;
    targetName?: string;
}

const connectionTypes = [
    { label: 'Ethernet', value: 'ethernet' },
    { label: 'Fiber', value: 'fiber' },
    { label: 'Wireless', value: 'wireless' },
    { label: 'Serial', value: 'serial' }
];

export default function TopologyLinkModal({ open, onClose, onConfirm, sourceName, targetName }: TopologyLinkModalProps) {
    const [connectionType, setConnectionType] = useState('ethernet');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!open) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        setTimeout(() => {
            onConfirm({
                connection_type: connectionType,
                is_uplink: false
            });
            setIsSubmitting(false);
            setConnectionType('ethernet');
        }, 300);
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
            {/* Removed overflow-hidden to allow dropdown to spill out */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl max-w-sm w-full transform transition-all animate-fadeIn">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-800/50 rounded-t-xl">
                    <div className="flex items-center gap-2">
                        <ArrowRightLeft className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">Link Assets</h3>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    {sourceName && targetName && (
                        <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/50 p-3 rounded-lg flex items-center justify-between">
                            <span className="font-mono text-blue-600 dark:text-blue-300 truncate max-w-[40%]">{sourceName}</span>
                            <ArrowRightLeft className="w-3 h-3 mx-2 text-gray-400 dark:text-gray-500" />
                            <span className="font-mono text-green-600 dark:text-green-300 truncate max-w-[40%]">{targetName}</span>
                        </div>
                    )}

                    <div className="relative z-50"> {/* Added z-index wrapper for dropdown */}
                        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            <Network className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400" />
                            Connection Type
                        </label>
                        <CustomDropdown
                            data={connectionTypes}
                            selectedValue={connectionType}
                            onSelect={setConnectionType}
                            placeholder="Select Type"
                            width="w-full"
                            buttonHeight={10}
                        />
                    </div>

                    <div className="flex gap-3 pt-2 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                            {isSubmitting ? 'Linking...' : 'Connect'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
